import { useState } from 'react';
import {
  faPlay,
  faSpinnerThird,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import ReactPlayer from 'react-player';
import { MediaGallery } from '@design-system/components/mediaGallery';
import { PostMediaItem } from '../model/post.types';
import { PostAudio } from './PostAudio';

type Props = {
  media: PostMediaItem[];
};

const tileBox =
  'relative grid h-[180px] min-w-[118px] shrink-0 place-items-center gap-1 rounded-[10px] bg-gray-100 text-gray-500';

export const PostMedia = ({ media }: Props) => {
  const [open, setOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  if (media.length === 0) return null;

  const visualItems = media.filter((item) => item.type !== 'audio');
  const audioItems = media.filter((item) => item.type === 'audio');

  // Images open the fullscreen gallery; videos play inline (the gallery cannot
  // play Mux HLS). Only ready images are interactive.
  const galleryImages = visualItems.filter(
    (item): item is PostMediaItem & { type: 'image' } =>
      item.type === 'image' && item.status === 'ready',
  );

  const openImage = (item: PostMediaItem) => {
    const index = galleryImages.findIndex((image) => image === item);
    if (index < 0) return;
    setStartIndex(index);
    setOpen(true);
  };

  const single = visualItems.length === 1;

  return (
    <>
      {visualItems.length > 0 && (
        <div
          className={classNames(
            'flex gap-2.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]',
            single ? 'justify-center' : 'justify-start overflow-x-scroll',
          )}
        >
          {visualItems.map((item, index) => {
            if (item.status === 'processing') {
              return (
                <div key={`processing-${index}`} className={tileBox}>
                  <FontAwesomeIcon icon={faSpinnerThird} spin />
                  <span className="text-xs">Processing…</span>
                </div>
              );
            }

            if (item.status === 'failed') {
              return (
                <div key={`failed-${index}`} className={tileBox}>
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <span className="text-xs">Upload failed</span>
                </div>
              );
            }

            if (item.type === 'video') {
              return (
                <div
                  key={item.url}
                  className={classNames(
                    'shrink-0 overflow-hidden rounded-[10px] bg-black',
                    single
                      ? 'h-[300px] w-full max-w-[533px]'
                      : 'h-[180px] w-[320px]',
                  )}
                >
                  <ReactPlayer
                    url={item.url}
                    light={item.thumbnailUrl}
                    controls
                    playsinline
                    width="100%"
                    height="100%"
                    playIcon={
                      <FontAwesomeIcon
                        icon={faPlay}
                        className="text-3xl text-white"
                      />
                    }
                  />
                </div>
              );
            }

            return (
              <img
                key={item.url}
                src={item.url}
                loading="lazy"
                onClick={() => openImage(item)}
                className={classNames(
                  'cursor-pointer rounded-[10px] object-cover',
                  single ? 'max-h-[486px]' : 'max-h-[180px]',
                )}
              />
            );
          })}
        </div>
      )}

      {audioItems.map((item) => (
        <PostAudio key={item.url} url={item.url} />
      ))}

      <MediaGallery
        open={open}
        items={galleryImages}
        startIndex={startIndex}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
